import {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  CSSProperties,
  useRef
} from 'react';
import { CSVLink } from 'react-csv';
import { Icon } from 'semantic-ui-react';
import { ColumnType, ExtendedColumnDef } from '../../entities/types';
import { BasicEntity, SimpleObject } from '../../types';
import { convertArrayOfObjectToFieldsMatrix, downloadXLSX } from '../../utils/export';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import {
  AdditionalTableActionsFunc,
  AdditionalTableProps,
  BodyRowProps,
  CRUDAllowedActions,
  InitialState,
  MRT_Row_WithOriginal
} from '../types';
import {
  useMaterialReactTable,
  MRT_RowData,
  MaterialReactTable,
  MRT_TableInstance
} from 'material-react-table';
import { Box, CircularProgress } from '@mui/material';
import { warningPopAlert } from '../PopAlert';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import '../../css/AppHome.css';
import '../../css/vdrGenerics.css';

// Estilos de marca para material-react-table (solo aspecto).
const TABLE_PAPER_SX = {
  borderRadius: 0,
  boxShadow: 'none',
  backgroundColor: 'var(--vdr-white)',
  '& .MuiTableBody-root .MuiTableRow-root:hover > td:after': {
    backgroundColor: 'var(--vdr-cyan-tint)'
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: 14
  },
  // Columnas fijadas con fondo opaco (si no, se transparenta el texto de las columnas de abajo)
  // y sin la sombra de borde: la separación la da la línea de la celda.
  '& td[data-pinned="true"]:before, & th[data-pinned="true"]:before': {
    backgroundColor: 'var(--vdr-white)',
    boxShadow: 'none'
  }
};

// Barra de desplazamiento horizontal siempre visible (las barras personalizadas de WebKit no se
// ocultan como las superpuestas) y con los grises de la marca; Firefox usa scrollbar-color.
const TABLE_CONTAINER_SX = {
  maxHeight: '500px',
  '@supports not selector(::-webkit-scrollbar)': {
    scrollbarWidth: 'auto',
    scrollbarColor: 'var(--vdr-mute-2) var(--vdr-soft)'
  },
  '&::-webkit-scrollbar': { height: 12, width: 12 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'var(--vdr-soft)' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'var(--vdr-mute-2)',
    border: '3px solid var(--vdr-soft)'
  },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'var(--vdr-mute)' }
};

// Bajo 1366 px las celdas van con 8 px a los lados para que la tabla de estudios quepa a 1280.
const NARROW_CELL_PADDING = {
  '@media (max-width: 1365px)': { paddingLeft: '8px', paddingRight: '8px' }
};

const TABLE_HEAD_CELL_SX = {
  backgroundColor: 'var(--vdr-white)',
  paddingTop: '12px',
  paddingBottom: '10px',
  paddingLeft: '10px',
  paddingRight: '10px',
  verticalAlign: 'bottom',
  ...NARROW_CELL_PADDING
};

const TABLE_TOP_TOOLBAR_SX = {
  backgroundColor: 'var(--vdr-white)',
  paddingLeft: 0,
  paddingRight: 0,
  '& .MuiIconButton-root': { color: 'var(--vdr-text-2)' },
  '& .MuiIconButton-root:hover': {
    color: 'var(--vdr-ink)',
    backgroundColor: 'var(--vdr-soft)'
  }
};

const TABLE_BOTTOM_TOOLBAR_SX = {
  backgroundColor: 'var(--vdr-white)',
  boxShadow: 'none',
  borderTop: '1px solid var(--vdr-line)',
  '& .MuiTablePagination-root, & .MuiInputBase-root, & .MuiTypography-root': {
    fontFamily: 'var(--vdr-font-body)'
  }
};

const CustomExport: FunctionComponent<{
  exportDataHandler: (when?: string) => Promise<any[]>;
  columnTypes: ColumnType[];
  fileName?: string;
}> = ({ exportDataHandler, columnTypes, fileName }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const exportData = async () => {
    setLoading(true);

    const data = await exportDataHandler();

    if (data.length === 0) {
      setLoading(false);
      return warningPopAlert(`No se encontraron registros que exportar`);
    }

    downloadXLSX(data, columnTypes, fileName);

    setLoading(false);
    return;
  };

  return (
    <button className='primary-button-style' onClick={exportData} disabled={loading}>
      <FileDownloadOutlinedIcon />
      Descargar Excel
    </button>
  );
};

export const ExportCSV: FunctionComponent<{
  onExport: () => string[][];
}> = ({ onExport }) => (
  <CSVLink
    style={{
      display: 'inline-block',
      padding: '10px 20px',
      marginInline: '1em',
      fontSize: '16px',
      fontWeight: 'bold',
      textDecoration: 'none',
      color: '#fff',
      backgroundColor: '#4CAF50',
      borderRadius: '5px',
      border: 'none',
      cursor: 'pointer'
    }}
    data={onExport()}
    filename={`export_${new Date().toISOString().split('T')[0]}.csv`}
    separator={';'}>
    <Icon name={'download'} />
  </CSVLink>
);

const Export: FunctionComponent<{
  onExport: () => any[][];
  columnTypes: ColumnType[];
}> = ({ onExport, columnTypes }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const handleExport = () => {
    setLoading(true);

    setTimeout(() => {
      const data = onExport();

      downloadXLSX(data, columnTypes);

      setLoading(false);
    }, 500);
  };

  return (
    <button className='primary-button-style' onClick={handleExport} disabled={loading}>
      <FileDownloadOutlinedIcon />
      Descargar Excel
    </button>
  );
};

type Props<T> = {
  columns: ExtendedColumnDef<T>[];
  tableData: MRT_RowData[];
  fetchData: () => Promise<any>;
  addAction?: JSX.Element;
  addMultiLineAction?: JSX.Element;
  deleteAction?: (
    getSelectedData: () => { ids: string[]; refs: string },
    table: MRT_TableInstance<MRT_RowData>
  ) => JSX.Element;
  secondaryAction?: AdditionalTableActionsFunc<T>;
  allowedActions?: Partial<CRUDAllowedActions<T>>;
  tableName?: string;
  editAction?: (row: BasicEntity) => JSX.Element;
  additionalTableProps?: AdditionalTableProps<T>;
  customExport?: {
    exportDataHandler: (when?: string) => Promise<any[]>;
    columnTypes: ColumnType[];
    fileName?: string;
  };
  initialState?: InitialState;
  enablePagination?: boolean;
  enableColumnVirtualization?: boolean;
  enableRowVirtualization?: boolean;
  optionsPagination?: {
    initialStatePagination?: {
      pagination?: { pageSize: number; pageIndex: number };
    };
  };
  columnsHeaderStyle?: CSSProperties;
  loadingTable?: boolean;
  additionalTopTableActions?: JSX.Element;
  enableStickyFooter?: boolean;
  bodyRowProps?: BodyRowProps;
};

const GenericTable = <T,>({
  // Required
  columns,
  tableData,
  fetchData,
  // Optionals
  enablePagination = true,
  addAction,
  addMultiLineAction,
  deleteAction,
  editAction,
  secondaryAction,
  allowedActions,
  tableName = '',
  customExport,
  columnsHeaderStyle,
  initialState,
  enableColumnVirtualization = false,
  enableRowVirtualization = false,
  optionsPagination,
  loadingTable,
  additionalTopTableActions,
  enableStickyFooter = false,
  bodyRowProps
}: Props<T>) => {
  const [isloading, setLoading] = useState<boolean>(true);

  // Mount Flag
  const isMounted = useRef(true);

  const {
    add: canAdd,
    multiLineForm: canMultiLine,
    delete: canDelete,
    export: canExport,
    select: canSelect,
    edit: canEdit,
    editCondition
  } = {
    add: true,
    multiLineForm: true,
    delete: true,
    export: false,
    select: false,
    edit: true,
    ...allowedActions
  };

  const fetchDataPage = useMemo(
    () => async () => {
      setLoading(true);

      await fetchData();
      if (!isMounted.current) {
        return;
      }

      setLoading(false);
    },
    [fetchData]
  );

  // ComponentDidMount
  useEffect(() => {
    // COMPONENT DID UNMOUNT
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ComponentDidUpdate (fetchDataPage)
  useEffect(() => {
    fetchDataPage();
  }, [fetchDataPage]);

  useEffect(() => {
    let isTime: NodeJS.Timeout;
    if (!loadingTable) {
      isTime = setTimeout(() => {
        setLoading(false);
      }, 100);
    }

    return () => {
      clearTimeout(isTime);
    };
  }, [loadingTable]);

  const renderExport = useCallback(
    (data: SimpleObject[], columns: ExtendedColumnDef<T>[]) => {
      if (canExport) {
        if (customExport) {
          return (
            <CustomExport
              exportDataHandler={customExport.exportDataHandler}
              columnTypes={customExport.columnTypes}
              fileName={customExport.fileName}
            />
          );
        }
        return (
          <Export
            onExport={() =>
              convertArrayOfObjectToFieldsMatrix(
                data,
                columns.map((obj) => obj.accessorKey),
                columns.map((obj) => obj.header)
              )
            }
            columnTypes={columns.map((obj) => obj.columnType)}
          />
        );
      }
      return <></>;
    },
    [canExport, customExport]
  );

  const column = useMemo(() => columns, [columns]);

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<MRT_RowData> }) => {
      const actions: Array<JSX.Element> = [];

      const visibleColumns = table.getVisibleFlatColumns().map((obj) => obj.id);
      const rows = table.getFilteredRowModel().rows.map((r: any) => r.original);
      const exportColumns = columns.filter(
        (obj) =>
          typeof obj.accessorKey === 'string' &&
          visibleColumns.includes(obj.accessorKey) &&
          !obj.omitExport
      );

      const data = rows.map((row) =>
        exportColumns.reduce((acc, obj) => {
          const key = obj.accessorKey as string;
          acc[key] = obj.valueToExport
            ? obj.valueToExport(row)
            : row.hasOwnProperty(key)
            ? row[key]
            : '';
          return acc;
        }, {} as Record<string, any>)
      );

      actions.push(renderExport(data, exportColumns));

      if (canDelete && deleteAction) {
        const getSelectedData = () => {
          const rowsToDelete = table.getSelectedRowModel().rows.map((row) => row.original);
          const ids = rowsToDelete.map((row) => row._id);
          const refs = `¿Seguro que deseas eliminar ${rowsToDelete.length} registros?`;

          return { ids, refs };
        };

        if (
          table.getIsSomeRowsSelected() ||
          table.getIsAllRowsSelected() ||
          table.getGroupedSelectedRowModel().rows.length > 0 ||
          table.getGroupedSelectedRowModel().flatRows.length > 0
        ) {
          actions.push(deleteAction(getSelectedData, table));
        }
      }
      if (secondaryAction) {
        actions.push(
          secondaryAction({
            selected: table.getSelectedRowModel().rows as MRT_Row_WithOriginal<T>[]
          })
        );
      }
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            flex: 1
          }}>
          {actions.map((action, idx) => (
            <Fragment key={idx}>{action}</Fragment>
          ))}
        </Box>
      );
    },
    [columns, canDelete, deleteAction, renderExport, secondaryAction]
  );

  const table = useMaterialReactTable({
    data: tableData,
    columns: column,
    enableGrouping: true,
    enablePagination: enablePagination,
    enableSelectAll: canDelete || canSelect,
    layoutMode: 'grid',
    enableRowSelection: canDelete || canSelect,
    positionToolbarAlertBanner: 'bottom',
    selectAllMode: 'all',
    enableStickyFooter,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'Acciones',
        minSize: 80,
        maxSize: 100
      },
      'mrt-row-select': {
        minSize: 42,
        maxSize: 60
      },
      'mrt-row-expand': {
        header: 'Expandir',
        minSize: 80,
        maxSize: 100,
        enableResizing: true
      }
    },
    initialState: {
      grouping: initialState?.grouping,
      columnVisibility: initialState?.columnVisibility || {},
      columnPinning: {
        left: [
          'mrt-row-actions',
          'mrt-row-select',
          'mrt-row-expand',
          ...(initialState?.columnPinning?.left || [])
        ],
        right: initialState?.columnPinning?.right
      },
      pagination: {
        pageSize: optionsPagination?.initialStatePagination?.pagination?.pageSize || 100,
        pageIndex: optionsPagination?.initialStatePagination?.pagination?.pageIndex || 0
      },
      density: 'compact',
      showGlobalFilter: true
    },
    enableBottomToolbar: true,
    enableColumnResizing: true,
    enableGlobalFilterModes: true,
    enableColumnPinning: true,
    enableColumnVirtualization: enableColumnVirtualization,
    enableRowVirtualization: enableRowVirtualization,
    localization: MRT_Localization_ES,
    enableStickyHeader: true,
    enableColumnActions: true,
    enableExpandAll: false,
    enableColumnOrdering: true,
    positionGlobalFilter: 'left',
    muiSearchTextFieldProps: {
      fullWidth: true
    },
    muiTableContainerProps: { sx: TABLE_CONTAINER_SX },
    // Aspecto (marca): sin sombra, fila al pasar el cursor en cian claro, barras de herramientas planas.
    muiTablePaperProps: { elevation: 0, sx: TABLE_PAPER_SX },
    muiTopToolbarProps: { sx: TABLE_TOP_TOOLBAR_SX },
    muiBottomToolbarProps: { sx: TABLE_BOTTOM_TOOLBAR_SX },
    muiTableBodyCellProps: ({ table }) => ({
      sx:
        table.getState().density === 'compact'
          ? {
              paddingTop: '9px',
              paddingBottom: '9px',
              paddingLeft: '10px',
              paddingRight: '10px',
              ...NARROW_CELL_PADDING
            }
          : NARROW_CELL_PADDING
    }),
    enableRowActions: canEdit,
    renderRowActions:
      canEdit && editAction
        ? ({ row }) => {
            if (editCondition && !editCondition(row?.original as T)) {
              return undefined;
            }
            return (
              <Box
                sx={{
                  display: 'flex',
                  gap: '1vw',
                  width: '100%',
                  justifyContent: 'center'
                }}>
                {editAction(row?.original as BasicEntity)}
              </Box>
            );
          }
        : undefined,
    renderTopToolbarCustomActions: (props) => renderTopToolbarCustomActions(props),
    getRowId: (row) => row._id,
    muiTableHeadCellProps: () => ({
      sx: { ...TABLE_HEAD_CELL_SX, ...columnsHeaderStyle }
    }),
    muiTableBodyRowProps: bodyRowProps
  });

  return (
    <div className='generic-table-container vdr-gen-table'>
      <div className='generic-table-top-container vdr-gen-table-head'>
        <h2 className='generic-table-title'>{tableName}</h2>

        <div className='generic-table-actions-container'>
          {additionalTopTableActions && additionalTopTableActions}
          {canMultiLine && addMultiLineAction}
          {canAdd && addAction}
        </div>
      </div>
      {isloading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={50} />
        </Box>
      ) : (
        <MaterialReactTable table={table} />
      )}
    </div>
  );
};

export default GenericTable;
